import { combineEpics, Epic, ofType } from 'redux-observable'
import { EMPTY, of } from 'rxjs'
import {
  catchError,
  filter,
  map,
  mergeMap,
  mergeMapTo,
  switchMap,
  withLatestFrom
} from 'rxjs/operators'
import { getHub } from '../../helpers/hub'
import { createOrderFromResponse } from '../order/helpers'
import { Order, OrderResponse } from '../order/types'
import { querySearch$ } from '../searchSecurities/epic'
import { RemoveGridAction } from '../securities/actions'
import { QuoteReliability } from '../securities/types'
import { addToast } from '../toasts/actions'
import { Toast } from '../toasts/types'
import { getIsOpenFin } from '../webSettings/selectors'
import { logError } from '../ws/actions'
import {
  addOrUpdateDepthOfMarket,
  FetchDepthAction,
  ForceCancelAction,
  forceCancelFailure,
  forceCancelSuccess,
  loadSecurityFailure,
  MarkAsRestrictedAction,
  markAsRestrictedFailure,
  markAsRestrictedSuccess,
  MarkAsUnrestrictedAction,
  markAsUnrestrictedFailure,
  markAsUnrestrictedSuccess,
  NewSubscriptionAction,
  PopoutSecurityAction,
  RemoveSubscriptionAction,
  resetDOMForGrid,
  setDepthOrders,
  setDomLimitExceeded,
  SetDomSecurityByQueryAction,
  updatePopouts
} from './actions'
import { getDOMLimitExceeded, getMaxDOMs } from './selectors'

export const SECURITIES_BUFFER_DELAY = 100

const getDepthOfMarketForSecurity = (
  securityId: number,
  quoteReliability: QuoteReliability,
  gridIndex: number,
  popout: boolean
) => {
  return getHub()
    .invoke<OrderResponse[]>(
      'SubscribeDepth',
      securityId,
      quoteReliability,
      gridIndex,
      popout
    )
    .pipe(
      map((orderResponses) =>
        orderResponses.map((o: OrderResponse) => createOrderFromResponse(o))
      )
    )
}

const getDepthOfMarket = () => {
  return getHub()
    .stream<Record<number, OrderResponse[]>>('GetDepth')
    .pipe(
      map((orderResponses: Record<number, OrderResponse[]>) => {
        const retval: Record<number, Order[]> = {}
        for (const gridIndex in orderResponses) {
          if (orderResponses.hasOwnProperty(gridIndex)) {
            retval[gridIndex] = orderResponses[gridIndex].map(
              (o: OrderResponse) => createOrderFromResponse(o)
            )
          }
        }
        return retval
      })
    )
}

const unsubscribeFromDepthOfMarketEpic: Epic = (action$) => {
  return action$.pipe(
    ofType<RemoveSubscriptionAction>('depthofmarket.removeSubscription'),
    mergeMap((action) => {
      const { gridIndex, securityId, popout } = action.payload
      return getHub()
        .invoke<OrderResponse[]>(
          'UnsubscribeDepth',
          gridIndex,
          securityId,
          popout
        )
        .pipe(mergeMapTo(EMPTY))
    })
  )
}

const getDepthEpic: Epic = (action$) => {
  return action$.pipe(
    ofType<FetchDepthAction>('depthofmarket.fetchDepth'),
    mergeMap((action) => {
      return getDepthOfMarket().pipe(
        map((records) => setDepthOrders(records)),
        catchError((err) => of(logError(err)))
      )
    })
  )
}

const subscribeToDepthOfMarketEpic: Epic = (action$) => {
  return action$.pipe(
    ofType<NewSubscriptionAction>('depthofmarket.newSubscription'),
    mergeMap((action) => {
      const { gridIndex, securityId, quoteReliability, popout } = action.payload
      return getDepthOfMarketForSecurity(
        securityId,
        quoteReliability,
        gridIndex,
        popout
      ).pipe(
        map((orders) =>
          addOrUpdateDepthOfMarket(gridIndex, securityId, orders)
        ),
        catchError((err) =>
          of(loadSecurityFailure(err, securityId), logError(err))
        )
      )
    })
  )
}

const resetDOMForGridEpic: Epic = (action$) =>
  action$.pipe(
    ofType<RemoveGridAction>('securities.removeGrid'),
    map((action) => resetDOMForGrid(action.payload.gridIndex))
  )

const markAsRestrictedEpic: Epic = (action$, state$) =>
  action$.pipe(
    ofType<MarkAsRestrictedAction>('depthofmarket.markAsRestricted'),
    mergeMap((action) => {
      const { id } = action.payload
      return getHub()
        .invoke('MarkAsRestricted', id)
        .pipe(
          map((errors: any) => markAsRestrictedSuccess(errors)),
          catchError((err) => of(markAsRestrictedFailure(err), logError(err)))
        )
    })
  )

const markAsUnrestrictedEpic: Epic = (action$, state$) =>
  action$.pipe(
    ofType<MarkAsUnrestrictedAction>('depthofmarket.markAsUnrestricted'),
    mergeMap((action) => {
      const { id } = action.payload
      return getHub()
        .invoke('MarkAsUnrestricted', id)
        .pipe(
          map((errors: any) => markAsUnrestrictedSuccess(errors)),
          catchError((err) => of(markAsUnrestrictedFailure(err), logError(err)))
        )
    })
  )

const forceCancelEpic: Epic = (action$, state$) =>
  action$.pipe(
    ofType<ForceCancelAction>('depthofmarket.forceCancel'),
    mergeMap((action) => {
      const { id } = action.payload
      return getHub()
        .invoke('ForceCancel', id)
        .pipe(
          map((errors: any) => forceCancelSuccess(errors)),
          catchError((err) => of(forceCancelFailure(err), logError(err)))
        )
    })
  )

const setDomSecurityByQuery: Epic = (action$) =>
  action$.pipe(
    ofType<SetDomSecurityByQueryAction>('depthofmarket.setDomSecurityByQuery'),
    switchMap((action) => {
      const { query, oldSecurityId } = action.payload
      const search$ = querySearch$(query)
      return search$.pipe(
        map((securities) =>
          securities.length
            ? updatePopouts(oldSecurityId, securities[0].id)
            : EMPTY
        )
      )
    })
  )

const domExceededEpic: Epic = (action$, state$) =>
  action$.pipe(
    ofType<PopoutSecurityAction>('depthofmarket.popout'),
    withLatestFrom(state$),
    filter(
      ([, state]) =>
        getDOMLimitExceeded(state) && (!getIsOpenFin(state) || fin.me.isView)
    ),
    mergeMap(([, state]) => {
      const maxDOMs = getMaxDOMs(state)
      const contents: Toast = {
        message: `Maximum amount of depth windows (${maxDOMs}) have been opened. Please close some windows before opening more.`,
        options: {
          toastId: `domlimit`,
          type: 'warning',
          onClose: () => {
            setDomLimitExceeded(false)
          }
        }
      }
      return of(addToast(contents))
    })
  )

export default combineEpics(
  subscribeToDepthOfMarketEpic,
  resetDOMForGridEpic,
  unsubscribeFromDepthOfMarketEpic,
  getDepthEpic,
  markAsRestrictedEpic,
  markAsUnrestrictedEpic,
  forceCancelEpic,
  setDomSecurityByQuery,
  domExceededEpic
)
